import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { VisitorTypeUploadReason } from '@app/models/visitortypeuploadreason.model';
import { Language } from '@app/models/language.model';
import { LocalizationService } from '@app/components/internationalization/localization.service';
import { ReferenceService } from '@app/services/reference/reference.service';
import { Subscription } from 'rxjs';
import { environment } from '@env/environment';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Translation } from '@ngx-translate/core';

@Component({
  selector: 'app-upload-reason-add-edit',
  templateUrl: './upload-reason-add-edit.component.html',
  styleUrl: './upload-reason-add-edit.component.scss',
  standalone: false
})
export class UploadReasonAddEditComponent implements OnInit, OnDestroy {
  private routeSubscription: Subscription;
  public reasonForm: FormGroup;
  uploadReasons: VisitorTypeUploadReason[] = [];
  isEditMode: boolean = false;
  languages: Language[] = [];
  uploadReasonId: string | null = null;
  visitorTypeId: string | null = null;
  uploadReasonData: VisitorTypeUploadReason | null = null;
  
  constructor(private route: ActivatedRoute,
    private _router: Router,
    private _localizationService: LocalizationService,
    private _formBuilder: FormBuilder,
    private _referenceService: ReferenceService) {
      this.routeSubscription = this.route.queryParamMap.subscribe((params: ParamMap) => {
        this.uploadReasonId = params.get('id'); 
        if(this.uploadReasonId){
          this.isEditMode = true;
        }
        this.visitorTypeId = params.get('visitortypeid'); 
      });
  }

 async ngOnInit(): Promise<void> {
    this.initForm();
    this._localizationService.getLanguages().subscribe({
      next: async (langs) => {
        this.languages = [...langs.filter(lang => lang.code !== environment.defaultLang)];

        this.initLanguageFormControls();
        this.initData();
      }
    });
    
  }

  initForm(){
    this.reasonForm = this._formBuilder.group({
      description: ['', [Validators.required, Validators.maxLength(250)]],
      isRequired: [false],
      isActive: [true]
    });
  }

  initLanguageFormControls(){
    if(this.languages.length > 0){
      this.languages.forEach(lang => {
        const langCode = lang.code.replace('-', '_');
        this.reasonForm.addControl(langCode, this._formBuilder.control('', [Validators.maxLength(250)]));
      });
    }
  }

  initData(){
    if(this.isEditMode){
      
      this._referenceService.getVisitorTypeUploadReason(this.uploadReasonId!).subscribe({
        next: async (reason) => {
          this.uploadReasonData = reason;
          this.reasonForm.patchValue({
            description: this.uploadReasonData?.description,
            isRequired: this.uploadReasonData?.isRequired,
            isActive: this.uploadReasonData?.isActive
          });

          if(this.uploadReasonData?.translations){
            this.uploadReasonData?.translations.forEach(translation => {
              const langCode = translation.languageCode.replace('-', '_');
              this.reasonForm.patchValue({[langCode]: translation.description });
            });
          }

          this.reasonForm.markAsPristine();
        }
      });

    }
  }

  back() {
    if(this.visitorTypeId){
      this._router.navigate(['/visitortypeaddeditmanagement', this.visitorTypeId]);
    }else{
      this._router.navigate(['visitortypemanagement']);
    }
  }

  disableSaveOnClick(): boolean{
    return this.reasonForm.invalid || this.reasonForm.pristine;
  }

  saveOnClick(){
    if(this.reasonForm.valid){
      const reasonData = this.reasonForm.value;
      const updatedVisitorType: VisitorTypeUploadReason = {
        id: this.uploadReasonId ? this.uploadReasonId : undefined,
        description: reasonData.description,
        isRequired: reasonData.isRequired,
        isActive: reasonData.isActive,
        visitorTypeId: this.visitorTypeId ? this.visitorTypeId : undefined,
        translations: []
      };

      this.languages.forEach(lang => {
        const langCode = lang.code.replace('-', '_');
        const newTransValue = this.reasonForm.get(langCode)?.value;
        if(newTransValue){
          let newTranslation : Translation = { languageCode: lang.code, description: newTransValue };
          updatedVisitorType.translations?.push(newTranslation);
        }
      });

      if(this.isEditMode){
        this.update(updatedVisitorType);
      }else{
        this.create(updatedVisitorType);
      }

    }
  }

  create(updatedVisitorType: VisitorTypeUploadReason){
      this._referenceService.addVisitorTypeUploadReason(updatedVisitorType).subscribe({
        next: (response) => {
          if(response){
            alert(this._localizationService.translate("upload_reasons_management_add_edit_save_success"));
            this.back();
          }
        },
        error: (error) => {
          if (error.error && error.error.errorCode) {
            alert(this._localizationService.translate(error.error.errorCode));
          }
          else {
            alert(this._localizationService.translate("upload_reasons_management_add_edit_error_adding"));
          }
        }
      });
  }

  update(updatedVisitorType: VisitorTypeUploadReason){
       this._referenceService.updateVisitorTypeUploadReason(updatedVisitorType).subscribe({
        next: (response) => {
          if(response){
            alert(this._localizationService.translate("upload_reasons_management_add_edit_save_success"));
            this.back();
          }
        },
        error: (error) => {
          if (error.error && error.error.errorCode) {
            alert(this._localizationService.translate(error.error.errorCode));
          }
          else {
            alert(this._localizationService.translate("upload_reasons_management_add_edit_error_adding"));
          }
        }
      });
  }

  closeOnClick(){
    this.back();
  }

  hasError(form: FormGroup, controlName: string): boolean {
    const validationOutput = form.controls[controlName].hasError('maxlength');
    return validationOutput;
  }

  ngOnDestroy(): void {
    this.routeSubscription.unsubscribe();
  }

}
